import type { CubbyType } from './form'

/**
 * Where one response has got to inside a stateful cubby.
 *
 * The mirror of `SubmissionCubbyState` in the Rails API. Four cubbies share one
 * row there — registration, booking, hiring and quiz — because they ask the
 * same question in different words, and `kind` says which vocabulary applies.
 *
 * The status is the product-state predicate the automation rule DSL branches
 * on, so this list is a contract rather than decoration: a word the server does
 * not accept is a control that looks live and refuses every click.
 */
export const CUBBY_STATE_KINDS = ['registration', 'booking', 'hiring', 'quiz'] as const

export type CubbyStateKind = (typeof CUBBY_STATE_KINDS)[number]

/**
 * The registration vocabulary, in the order a row normally travels.
 *
 * The FIRST entry of each list below is the status a row is born in, which is
 * why the defaults are derived from it rather than written a second time.
 */
export const REGISTRATION_STATUSES = ['registered', 'checked_in', 'cancelled'] as const

export type RegistrationStatus = (typeof REGISTRATION_STATUSES)[number]

/** The booking vocabulary: a slot is asked for, agreed to, and then kept or not. */
export const BOOKING_STATUSES = ['requested', 'confirmed', 'cancelled', 'completed'] as const

export type BookingStatus = (typeof BOOKING_STATUSES)[number]

/**
 * The hiring vocabulary, in the order an application travels.
 *
 * It ends in 'rejected' and NOT in 'cancelled', which is the one place the two
 * new kinds stop being symmetrical: a booking is cancelled, an application is
 * rejected. `SubmissionCubbyState#status_belongs_to_kind` refuses the wrong
 * word, so borrowing one here would be a Select option that 422s on click.
 */
export const HIRING_STATUSES = [
  'applied',
  'screening',
  'interview',
  'offer',
  'hired',
  'rejected',
] as const

export type HiringStatus = (typeof HIRING_STATUSES)[number]

/**
 * The quiz vocabulary: three outcomes, not three stages.
 *
 * Nothing travels from 'passed' to 'failed' the way an application travels from
 * screening to offer — `Submissions::QuizHandler` writes the outcome once, and
 * the only later move is an owner overriding it by hand. 'unscored' is the
 * birth status and it is a real answer rather than a gap: an attempt whose
 * pinned answer-key snapshot has gone is left unscored on purpose, because the
 * alternative is scoring it against a key it was never sat under.
 */
export const QUIZ_STATUSES = ['unscored', 'passed', 'failed'] as const

export type QuizStatus = (typeof QUIZ_STATUSES)[number]

/** Every status this build can write. */
export type CubbyStatus = RegistrationStatus | BookingStatus | HiringStatus | QuizStatus

export const CUBBY_STATUSES: Record<CubbyStateKind, readonly CubbyStatus[]> = {
  registration: REGISTRATION_STATUSES,
  booking: BOOKING_STATUSES,
  hiring: HIRING_STATUSES,
  quiz: QUIZ_STATUSES,
}

/**
 * The kinds whose status an operator may set by hand.
 *
 * Three of the four, and the line is not arbitrary. A registration, a booking
 * and a hiring status is a DECISION somebody makes: an attendee checks in, a
 * slot is confirmed, a candidate is advanced. A quiz status is a COMPUTED FACT,
 * derived from a score against a versioned answer key at the pass mark the
 * attempt was judged under.
 *
 * `PATCH /api/submissions/:id/cubby_state` writes `status` alone, so accepting
 * one for a quiz produced rows reading `status=failed, passed=true,
 * percentage=100` — where `submission.quiz_status` and `submission.quiz_passed`
 * contradict each other and any automation rule branching on either is right by
 * luck. The API now refuses it with 422 `quiz_status_is_computed`, which makes
 * a quiz status Select a control that fails on every click.
 *
 * The way to move a quiz verdict is to correct the answer key and rescore.
 */
export const OPERATOR_SET_KINDS = ['registration', 'booking', 'hiring'] as const

export function isOperatorSetKind(kind: string): boolean {
  return (OPERATOR_SET_KINDS as readonly string[]).includes(kind)
}

export const CUBBY_DEFAULT_STATUS: Record<CubbyStateKind, CubbyStatus> = {
  registration: REGISTRATION_STATUSES[0],
  booking: BOOKING_STATUSES[0],
  hiring: HIRING_STATUSES[0],
  quiz: QUIZ_STATUSES[0],
}

const ALL_STATUSES: readonly string[] = Object.values(CUBBY_STATUSES).flat()

export function isCubbyStatus(value: unknown): value is CubbyStatus {
  return typeof value === 'string' && ALL_STATUSES.includes(value)
}

/**
 * The state kind a cubby writes, or null for the cubbies that keep no state.
 *
 * A membership test rather than a second mapping, exactly as
 * `SubmissionCubbyState.kind_for_cubby` does it: the stateful cubbies use their
 * own type as the kind, so a mapping would be a copy that can drift.
 */
export function cubbyStateKind(cubbyType: CubbyType): CubbyStateKind | null {
  return (CUBBY_STATE_KINDS as readonly string[]).includes(cubbyType)
    ? (cubbyType as CubbyStateKind)
    : null
}

/**
 * The row as the API sends it.
 *
 * The quiz scalars are on every row `as_api_json` builds, and they are null for
 * every kind but quiz. Optional here rather than required because a build of
 * this SPA can meet an older payload — and because a held response arrives with
 * `cubby_state: null` and never reaches this function at all.
 *
 * `breakdown` is deliberately NOT part of this payload: the per-question detail
 * is the analytics endpoint's job, and shipping it on every submission row
 * would put an unbounded jsonb blob on every list request.
 */
export interface CubbyStateFromAPI {
  id: number
  kind: string
  status: string
  occurs_at: string | null
  status_changed_at: string
  score?: number | null
  max_score?: number | null
  percentage?: number | null
  passed?: boolean | null
  attempt_number?: number | null
  /**
   * The form version whose snapshot this score was computed against.
   *
   * The central fact of the quiz cubby, and the reason it is carried all the
   * way to the panel rather than dropped here: an owner who corrects an answer
   * key and sees old scores unchanged has to be told WHY, or the product looks
   * broken at exactly the moment it is being most careful.
   */
  answer_key_version?: number | null
  /**
   * The pass mark THIS attempt was judged against, not the one the form carries
   * today.
   *
   * The answer key was versioned and the pass mark was not, which meant the
   * accepted rescore path was doing the very thing `?against=current` is
   * refused for: an attempt recorded `passed` at a mark of 40 flipped to
   * `failed` the moment the owner moved the mark to 90, with the answer key
   * never touched. `Submissions::QuizHandler` now snapshots the mark beside the
   * score, so `passed` is reproducible from the row alone.
   *
   * Null means "written before the column existed", and the only mark that
   * exists for such a row is the form's current one. Zero is NOT null: "record
   * the score, everybody passes" is a real setting.
   */
  pass_percentage?: number | null
  scored_at?: string | null
}

export interface CubbyState {
  id: number
  /**
   * The vocabulary this row's status belongs to, as the server named it.
   *
   * Left wide on purpose. The backend ships first, so a build of this SPA can
   * legitimately meet a kind it has not been taught yet, and narrowing by cast
   * would only move the lie somewhere harder to see.
   */
  kind: string
  /**
   * A word from the kind's list. Narrow on the way OUT (`updateCubbyState`
   * only accepts a status this build knows), tolerant on the way IN: an
   * unrecognised word renders as itself rather than erasing state the owner set.
   */
  status: string
  /** When the thing being registered for happens, or null when none is declared. */
  occursAt: string | null
  statusChangedAt: string
  /**
   * What the attempt scored, out of what, as a percentage — or null.
   *
   * Null for every kind but quiz, and null on a quiz attempt the scorer could
   * not resolve. Null is NOT zero: a brand new attempt with no answer key and
   * one that got everything wrong are different answers, and a panel that
   * collapsed them would report the first as a total failure.
   */
  score: number | null
  maxScore: number | null
  percentage: number | null
  passed: boolean | null
  attemptNumber: number | null
  /** The answer-key version this score was computed against. See above. */
  answerKeyVersion: number | null
  /** The pass mark this attempt was judged against, or null on an older row. */
  passPercentage: number | null
  scoredAt: string | null
}

export function transformCubbyState(data: CubbyStateFromAPI): CubbyState {
  return {
    id: data.id,
    kind: data.kind,
    status: data.status,
    occursAt: data.occurs_at ?? null,
    statusChangedAt: data.status_changed_at,
    // `?? null` and never `|| null`: a score of 0 and a percentage of 0 are
    // real results, and `passed: false` is the whole answer on a failed
    // attempt. `||` would turn all three into "not scored".
    score: data.score ?? null,
    maxScore: data.max_score ?? null,
    percentage: data.percentage ?? null,
    passed: data.passed ?? null,
    attemptNumber: data.attempt_number ?? null,
    answerKeyVersion: data.answer_key_version ?? null,
    passPercentage: data.pass_percentage ?? null,
    scoredAt: data.scored_at ?? null,
  }
}
